/**
 * Session scope — Sprint 14d.
 *
 * Provides the transient scope state (which entity references the session is
 * focused on). Persisted to localStorage per workspace. Provider mounted at
 * AppShell level. Any component can read/set scope via `useSessionScope()`.
 */

import { createContext, useContext, useState, useCallback, useEffect, useRef, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../lib/hooks/use-auth";

/**
 * Sprint 19 — query-key prefixes whose results depend on the active scope. When
 * scope changes we invalidate exactly these so the lists re-fetch with the new
 * `_scope` (injected at the mcp-client layer from localStorage). Centralised
 * here so no per-list hook needs to thread scope into its queryKey.
 */
const SCOPE_DEPENDENT_PREFIXES = ["agent-tasks", "my-tasks", "agent-routines", "activities"];

function isScopeDependentQuery(key: readonly unknown[]): boolean {
  const head = key[0];
  return typeof head === "string" && SCOPE_DEPENDENT_PREFIXES.includes(head);
}

// ---------------------------------------------------------------------------
// Types (mirrors worker ScopeRef)
// ---------------------------------------------------------------------------

export interface ScopeRef {
  kind: "entity";
  type: string;
  id: string;
}

interface SessionScopeContextValue {
  scope: ScopeRef[];
  setScope: (refs: ScopeRef[]) => void;
  clearScope: () => void;
}

const SessionScopeContext = createContext<SessionScopeContextValue>({
  scope: [],
  setScope: () => {},
  clearScope: () => {},
});

// ---------------------------------------------------------------------------
// localStorage helpers
// ---------------------------------------------------------------------------

function storageKey(workspaceId: string | null | undefined): string | null {
  return workspaceId ? `kuvio_session_scope_${workspaceId}` : null;
}

function loadScope(key: string | null): ScopeRef[] {
  if (!key) return [];
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (r: unknown) =>
        r && typeof r === "object" &&
        (r as Record<string, unknown>).kind === "entity" &&
        typeof (r as Record<string, unknown>).type === "string" &&
        typeof (r as Record<string, unknown>).id === "string",
    ) as ScopeRef[];
  } catch {
    return [];
  }
}

function persistScope(key: string | null, refs: ScopeRef[]): void {
  if (!key) return;
  try {
    if (refs.length === 0) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, JSON.stringify(refs));
    }
  } catch {
    /* ignore */
  }
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function SessionScopeProvider({ children }: { children: ReactNode }) {
  const { currentWorkspaceId } = useAuth();
  const queryClient = useQueryClient();
  const key = storageKey(currentWorkspaceId);
  const [scope, setScopeState] = useState<ScopeRef[]>(() => loadScope(key));

  useEffect(() => {
    setScopeState(loadScope(key));
  }, [key]);

  // Sprint 19: scope-change live reactivity. The list queryKeys don't include
  // scope (it rides on `_scope`, injected per-call from localStorage), so React
  // Query thinks cached data is fresh and won't re-fetch on a scope change.
  // Invalidate the scope-dependent lists so panels update immediately — the fix
  // for "changing scope doesn't update the UI". Skip the initial mount (the
  // queries are loading with the right scope already).
  const firstRun = useRef(true);
  const scopeSignal = JSON.stringify(scope);
  useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }
    queryClient.invalidateQueries({ predicate: (q) => isScopeDependentQuery(q.queryKey) });
  }, [scopeSignal, queryClient]);

  const setScope = useCallback(
    (refs: ScopeRef[]) => {
      setScopeState(refs);
      persistScope(key, refs);
    },
    [key],
  );

  const clearScope = useCallback(() => {
    setScopeState([]);
    persistScope(key, []);
  }, [key]);

  return (
    <SessionScopeContext.Provider value={{ scope, setScope, clearScope }}>
      {children}
    </SessionScopeContext.Provider>
  );
}

export function useSessionScope(): SessionScopeContextValue {
  return useContext(SessionScopeContext);
}

/**
 * Non-React accessor — reads the current scope from localStorage directly.
 * Used by mcp-client.ts to inject `_scope` without needing React context.
 */
export function getCurrentScope(workspaceId: string | null | undefined): ScopeRef[] {
  return loadScope(storageKey(workspaceId));
}
